import './Memberships.css';
import React, { useState } from 'react';
import { Card } from '../../../components/Card/Card';
import { useSmartConfirmation } from '../../../components/ConfirmationPrompt/ConfirmationPrompt';
import { Icon } from '../../../components/Icon/Icon';
import { ColumnContent, SortableList } from '../../../components/SortableList/SortableList';
import { Tooltip } from '../../../components/Tooltip/Tooltip';
import { t } from '../../../locales/translate';
import { Company, getCompanyDetails } from '../../../model/company';
import { useData } from '../../../model/data';
import { Membership, regenerateAPIToken, useSelectedMembership } from '../../../model/membership';
import { handleError, handleSuccess } from '../../../model/notification';
import { fullUserName } from '../../../model/user';
import { MembershipsForm } from '../MembershipsModal/MembershipsForm';
import { Stack } from '../../../components/Stack/Stack';
import { Modal } from '../../../components/Modal/Modal';
import { expiredDate } from '../../../util/datetime';
import { PermissionButton } from '../../../components/Button/PermissionButton';

interface Props {
  company: Company;
  onReloadActivities: () => void;
}

function getModalTitle(modalAction?: 'invite' | 'add', selectedMember?: boolean): string {
  if (modalAction === 'add') {
    if (selectedMember) {
      return t.model.membership.editRegisteredUser;
    } else {
      return t.model.membership.addRegisteredUser;
    }
  }
  if (modalAction === 'invite') {
    if (selectedMember) {
      return t.model.membership.editInvitation;
    } else {
      return t.model.membership.inviteNewUser;
    }
  }
  return '';
}

export function Memberships({ company, onReloadActivities }: Props): JSX.Element {
  const membership = useSelectedMembership();
  const [modalAction, setModalAction] = useState<'invite' | 'add'>();
  const [selectedMember, setSelectedMember] = useState<Partial<Membership> | undefined>(undefined);
  const memberships = useData((db) => {
    return db.table('memberships').where((mem) => mem.company.id === company.id);
  });
  const confirmation = useSmartConfirmation();

  function toggleModal(action?: 'invite' | 'add', member?: Partial<Membership>): void {
    setModalAction(action);
    setSelectedMember(member);
  }

  const [loading, setLoading] = useState(false);

  async function handleOnSuccess(): Promise<void> {
    onReloadActivities();
    toggleModal();
    setLoading(true);
    try {
      await getCompanyDetails(company.id);
    } catch (error) {
      handleError(error);
    }
    setLoading(false);
  }

  const canInvite = membership?.company.$permissions.includes('invite');
  const canCreateMembership = membership?.company.$permissions.includes('create_memberships');
  const canExecuteAnyActions = !!memberships.find((membership) => membership.$permissions.includes('update'));

  return (
    <>
      <Card
        title={t.model.membership._plural}
        actions={
          (canInvite || canCreateMembership) && (
            <Tooltip
              className="company-memberships__tooltip"
              mode="click"
              position="bottom-end"
              reference={<Icon name="plus" />}
            >
              <ul className="company-memberships__option-list">
                {canInvite && (
                  <li className="company-memberships__option" onClick={() => toggleModal('invite', {})}>
                    <Stack line size="xs">
                      <Icon name="plus-circle" />
                      <span>{t.model.membership.inviteNewUser}</span>
                    </Stack>
                  </li>
                )}

                {canCreateMembership && (
                  <li className="company-memberships__option" onClick={() => toggleModal('add', {})}>
                    <Stack line size="xs">
                      <Icon name="user-plus" />
                      <span>{t.model.membership.addRegisteredUser}</span>
                    </Stack>
                  </li>
                )}
              </ul>
            </Tooltip>
          )
        }
        fullbleed
      >
        <SortableList
          loading={loading}
          items={memberships.map((mem) => ({
            membership: mem,
            user: fullUserName(mem.user, { fallbackToEmail: true }),
            email: mem.user?.email,
            role: mem.role,
            comment: mem.comment,
            isAdmin: mem.permissions.includes('admin'),
            isMerchAdmin: mem.permissions.includes('merch_admin'),
            isTicketingAdmin: mem.permissions.includes('ticketing_admin'),
            isFinancialAdmin: mem.permissions.includes('financial'),
            invitedEmail: mem.invitedEmail,
            token: mem.token,
            action: '',
            calendarFeedUrl: mem.calendarFeedUrl,
          }))}
          columns={[
            {
              dataKey: 'user',
              label: t.model.user._name,
              cellRenderer: (row) => {
                if (row.invitedEmail) {
                  return <p>{row.invitedEmail}</p>;
                }
                return (
                  <>
                    <p>{row.user}</p>
                    <small className="company-memberships__small">{row.email}</small>
                  </>
                );
              },
            },
            {
              dataKey: 'role',
              label: t.model.membership.role,
              cellRenderer: (row) => t.model.membership[row.role],
            },
            {
              dataKey: 'comment',
              label: t.model.tour.comment,
              cellRenderer: (row) => {
                return (
                  <>
                    <p>{row.comment}</p>
                    {row.invitedEmail && (
                      <small className="company-memberships__small">
                        {t.model.membership.invitedExpires(row.membership.createdAt, row.membership.expiresAt)}
                      </small>
                    )}
                  </>
                );
              },
            },
            {
              dataKey: 'token',
              cellRenderer: (row) => {
                const token = row.token;
                return (
                  <>
                    {token && (
                      <Icon
                        name="link"
                        tooltip={t.model.membership.copyToken}
                        onClick={() => {
                          navigator.clipboard.writeText(token);
                          handleSuccess(t.model.membership.copyConfirmation);
                        }}
                      />
                    )}
                    {row.membership.$permissions.includes('regenerate_token') && (
                      <Icon
                        name="disconnect"
                        tooltip={t.model.membership.regenerateToken}
                        onClick={() =>
                          confirmation({
                            title: t.model.membership.regenerateToken,
                            message: t.model.membership.regenerateTokenConfirmation,
                            action: regenerateAPIToken,
                          })
                        }
                      />
                    )}
                  </>
                );
              },
              label: t.general.apiToken,
            },
            {
              dataKey: 'calendarFeedUrl',
              label: t.general.calendarFeedUrl,
              cellRenderer: (row) => {
                const calendarFeedUrl = row.calendarFeedUrl;

                if (!calendarFeedUrl) return null;

                return (
                  <Icon
                    name="calendar"
                    tooltip={t.model.membership.copyCalendarFeedUrlToClipboard}
                    onClick={() => {
                      navigator.clipboard.writeText(calendarFeedUrl);
                      handleSuccess(t.model.membership.copyCalendarFeedUrlToClipboardConfirmation);
                    }}
                  />
                );
              },
            },
            ...(company.slug === 'krasserstoff'
              ? ([
                  {
                    dataKey: 'isAdmin',
                    label: t.model.membership.admin,
                    cellRenderer: (row) => row.isAdmin && <Icon name="check" />,
                  },
                  {
                    dataKey: 'isMerchAdmin',
                    label: t.model.membership.merchAdmin,
                    cellRenderer: (row) => row.isMerchAdmin && <Icon name="check" />,
                  },
                  {
                    dataKey: 'isTicketingAdmin',
                    label: t.model.membership.ticketingAdmin,
                    cellRenderer: (row) => row.isMerchAdmin && <Icon name="check" />,
                  },
                  {
                    dataKey: 'isFinancialAdmin',
                    label: t.model.membership.financialAdmin,
                    cellRenderer: (row) => row.isFinancialAdmin && <Icon name="check" />,
                  },
                ] as ColumnContent<{
                  isAdmin: boolean;
                  isMerchAdmin: boolean;
                  isFinancialAdmin: boolean;
                }>[])
              : ([
                  {
                    dataKey: 'isFinancialAdmin',
                    label: t.model.membership.financialAdmin,
                    cellRenderer: (row) => row.isFinancialAdmin && <Icon name="check" />,
                  },
                ] as ColumnContent<{
                  isFinancialAdmin: boolean;
                }>[])),
            ...(canExecuteAnyActions
              ? ([
                  {
                    dataKey: 'action',
                    label: t.general.actions,
                    cellRenderer: (row) => (
                      <PermissionButton
                        name="edit"
                        style={{
                          color: 'var(--color-text-label)',
                          cursor: 'pointer',
                        }}
                        onClick={() => {
                          if (row.membership.invitedEmail) {
                            return toggleModal('invite', row.membership);
                          }
                          toggleModal('add', row.membership);
                        }}
                        model={row.membership}
                        permission={'update'}
                      />
                    ),
                  },
                ] as ColumnContent<{
                  action: string;
                  membership: Membership;
                }>[])
              : []),
          ]}
        />
      </Card>
      <Modal
        open={!!selectedMember}
        title={getModalTitle(modalAction, !!selectedMember?.id)}
        size="small"
        actions={
          modalAction === 'invite' && !!selectedMember?.id ? (
            expiredDate(selectedMember.expiresAt) ? (
              <p className="company-memberships__invitation">{t.model.membership.invitationExpired}</p>
            ) : (
              <p className="company-memberships__invitation">{t.model.membership.awaitingResponse}</p>
            )
          ) : undefined
        }
      >
        <MembershipsForm
          action={modalAction}
          membership={
            selectedMember?.id
              ? {
                  ...selectedMember,
                  userId: selectedMember.user?.id,
                }
              : {}
          }
          company={company}
          isKs={company.slug === 'krasserstoff'}
          onSuccess={handleOnSuccess}
          onClose={() => toggleModal()}
          isExpired={expiredDate(selectedMember?.expiresAt)}
        />
      </Modal>
    </>
  );
}
